import React, { useRef, useState } from "react";

/**
 * ZoomImage — Amazon-style hover magnifier. Moving the cursor over the image
 * magnifies the area under the cursor (background-position follows the pointer).
 * On touch devices it just shows the plain image (no hover), which is fine.
 *
 * Props: src, alt, zoom (magnification, default 2.5), className (wrapper sizing)
 */
export default function ZoomImage({ src, alt = "", zoom = 2.5, className = "" }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });

  const move = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const point = e.touches ? e.touches[0] : e;
    const x = ((point.clientX - r.left) / r.width) * 100;
    const y = ((point.clientY - r.top) / r.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  if (!src) return <div className={`bg-pearl ${className}`} />;

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden cursor-crosshair ${className}`}
      onMouseEnter={() => setOn(true)}
      onMouseLeave={() => setOn(false)}
      onMouseMove={move}
      onTouchStart={() => setOn(true)}
      onTouchEnd={() => setOn(false)}
      onTouchMove={move}
      data-testid="zoom-image"
    >
      {/* Base image (visible until zoom kicks in) */}
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover select-none pointer-events-none"
        style={{ opacity: on ? 0 : 1 }}
        draggable={false}
      />
      {/* Zoomed layer that follows the cursor */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${src})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: `${zoom * 100}%`,
          backgroundPosition: `${pos.x}% ${pos.y}%`,
          opacity: on ? 1 : 0,
          transition: "opacity 0.1s",
        }}
      />
    </div>
  );
}
